<script lang="ts" setup>
import { z } from 'zod'

const { createEvent } = useAnalytics()
const { t } = useI18n()
const billingStore = useBillingStore()
const teamStore = useTeamsStore()

const buying = ref(false)
const paymentWindow = ref<null | Window>(null)

const form = reactive({ credits: 20 * 1000 })
const state = ref<SubscriptionFormState>('default')

const validation = z.object({
  credits: z.number()
    .min(1000, { message: t('validation.min', { min: 1000 }) })
    .max(100 * 1000 * 1000, { message: t('validation.max', { max: 100 * 1000 * 1000 }) }),
})

const price = computed(() => {
  const creditsInThousands = Math.floor(form.credits / 1000)
  const pricePerThousand = teamStore.currentPlan.pricing.additionalCredits
  const result = Number((creditsInThousands * pricePerThousand).toFixed(2))

  if (result < 0) {
    return 0
  }

  return result
})

async function onCreateOneTimePayment() {
  buying.value = true

  try {
    const checkoutSession = await billingStore.createOneTimePayment({
      amount: price.value * 100,
      credits: form.credits,
    })

    if (checkoutSession) {
      createEvent({ name: '[Settings] One-Time Payment Started' })

      state.value = 'checkoutSessionInProgress'
      paymentWindow.value = window.open(checkoutSession.url!, '_blank')

      // Check if window is closed every second
      const paymentWindowMonitor = window.setInterval(() => {
        if (paymentWindow.value?.closed) {
          window.clearInterval(paymentWindowMonitor)
          state.value = 'default'
          paymentWindow.value = null
        }
      }, 500)
    }
  }
  catch (error) {
    console.error(error)
  }
  finally {
    buying.value = false
  }
}
</script>

<template>
  <UCard
    class="gradient-texture"
    :ui="{ base: '!border-none !ring-transparent !shadow-none' }"
  >
    <div class="flex flex-col gap-4">
      <div>
        {{ t('title') }}
      </div>

      <UForm
        class="flex flex-col items-start gap-2"
        :schema="validation"
        :state="form"
        :validate-on="['submit']"
        @submit="onCreateOneTimePayment"
      >
        <UFormGroup
          class="w-full"
          name="credits"
        >
          <UInput
            v-model="form.credits"
            autocomplete="off"
            :icon="getIcon('creditCard')"
            :placeholder="t('enterCredits')"
            size="md"
            step="1000"
            type="number"
          />
        </UFormGroup>

        <UButton
          block
          color="gray"
          :icon="getIcon('check')"
          :label="t('buy', { price })"
          :loading="buying"
          size="md"
          type="submit"
          :ui="{ icon: { base: 'text-green-600 dark:text-green-400' } }"
        />
      </UForm>

      <p
        class="text-sm"
        :class="{
          'text-amber-600 dark:text-amber-400': state === 'checkoutSessionInProgress',
          'text-gray-600 dark:text-gray-400': state === 'default',
        }"
      >
        {{ t(`states.${state}`, { price: teamStore.currentPlan.pricing.additionalCredits }) }}

        <template v-if="state === 'default' && !billingStore.subscriptionName.startsWith('enterprise')">
          {{ t('states.defaultUpgrade') }}
        </template>
      </p>
    </div>
  </UCard>
</template>

<i18n lang="json">
{
  "en": {
    "buy": "Buy (${price})",
    "states": {
      "checkoutSessionInProgress": "A checkout session has been opened in a new tab. Please complete the payment.",
      "default": "The current price per 1,000 credits for this plan is ${price}.",
      "defaultUpgrade": "You can also upgrade to a higher plan to get more credits."
    },
    "title": "Do you need more credits?"
  },
  "es": {
    "buy": "Comprar (${price})",
    "states": {
      "checkoutSessionInProgress": "Se ha abierto una sesión de pago en una nueva pestaña. Por favor, completa el pago.",
      "default": "El precio actual por 1,000 créditos para este plan es ${price}.",
      "defaultUpgrade": "También puedes actualizar a un plan más alto para obtener más créditos."
    },
    "title": "¿Necesitas más créditos?"
  },
  "fr": {
    "buy": "Acheter (${price})",
    "states": {
      "checkoutSessionInProgress": "Une session de paiement a été ouverte dans une nouvelle fenêtre. Veuillez compléter le paiement.",
      "default": "Le prix actuel pour 1,000 crédits pour ce plan est ${price}.",
      "defaultUpgrade": "Vous pouvez également mettre à niveau vers un plan plus élevé pour obtenir plus de crédits."
    },
    "title": "Voulez-vous plus de crédits ?"
  }
}
</i18n>
